import React, { useState } from "react";
import { Box } from "@mui/material";
import { Button, Col, Container, Modal, Row } from "react-bootstrap";
import { KTIcon } from "../../../_metronic/helpers";
import * as Yup from "yup";
import { useFormik } from "formik";
import { charValidate } from "../../../components/utility";
import { addCompany, uploadPicture } from "../../../api";
import { snackActions } from "../../../helpers/SnackUtilsConfigurator";
import { useMutation } from "react-query";
import { APP_URLs } from "../../../constants/appURLs";
import CloseIcon from '@mui/icons-material/Close';

const AddEditCompany = ({ show, handleClose, editData }) => {
  const [imgUploading, setImgUploading] = useState(false);

  let imgUrl = editData?.image
    ? editData?.image[0]
      ? editData?.image[0]
      : ""
    : "";

  const [companyImg, setCompanyImg] = useState("");

  const [imgFileName, setImgFileName] = useState(
    imgUrl ? imgUrl?.split("/img/")[1] : ""
  );

  const companyValidationSchema = Yup.object().shape({
    name: Yup.string().trim().required("Name is required"),
    image: Yup.string().required('Image is required'),
  });

  const { mutateAsync: addCompanyAPI, isLoading: loading } = useMutation(
    "add-company",
    addCompany
  );

  const formik = useFormik({
    initialValues: {
      name: editData ? editData?.name : "",
      image: editData ? editData?.image : "",
    },
    validationSchema: companyValidationSchema,
    onSubmit: async (values, { setFieldError }) => {
      if (!companyImg && !editData?.image) {
        setFieldError('image', 'Image is required');
        return;
      }
      let bodyParams = {
        id: editData ? editData?._id : undefined,
        name: values?.name ? values?.name?.replace(/^\s+|\s+$/g, "") : "",
        image: companyImg || (editData && editData.image) || null,
      };
      let response = await addCompanyAPI(bodyParams);
      if (response?.status) {
        snackActions.success(response?.message ?? "Company Added Successfully!");
        formik.resetForm();
        setImgFileName("");
        setCompanyImg("");
        handleClose(true);
      } else {
        snackActions.error(response?.message ?? "Something went wrong!");
      }
    },
  });

  const handleChangeImageUpload = async (e) => {
    let file = e?.target?.files[0];
    let formData = new FormData();
    setImgFileName(file?.name ?? "");
    formData.append("photo", file);
    if (file) {
      setImgUploading(true);
      let response = await uploadPicture(formData);
      if (response?.status) {
        setImgUploading(false);
        setCompanyImg(response?.data?.images);
        formik.setFieldValue('image', response?.data?.images);
      } else {
        snackActions.error(
          response?.message ?? "Issue in image upload, try again latter!"
        );
        setImgUploading(false);
      }
    }
  };

  const removeImg = () => {
    formik.setFieldValue('image', '');
    formik.setFieldError('image', null);
    setCompanyImg("");
    if (editData) {
      editData.image = "";
    }
    setImgFileName("");
  };

  return (
    <Box>
      <Modal
        id="kt_modal_create_app"
        tabIndex={-1}
        aria-hidden="true"
        dialogClassName="modal-dialog modal-dialog-centered mw-700px"
        show={show}
        backdrop="static"
      >
        <div className="modal-header">
          <h2> {editData ? "Edit" : "Add"} Company</h2>
          {/* begin::Close */}
          <div
            className="btn btn-sm btn-icon btn-active-color-primary"
            onClick={() => {
              setImgFileName("");
              setCompanyImg("");
              handleClose(false);
            }}
          >
            <KTIcon className="fs-1" iconName="cross" />
          </div>
          {/* end::Close */}
        </div>

        <div className="p-6" style={{ overflowY: "auto" }}>
          <form onSubmit={formik.handleSubmit} noValidate className="form">
            <div className="mb-6">
              <label className="form-label fw-bold">Name<span className="red-asterisk">*</span></label>
              <input
                type="text"
                className="form-control form-control-lg form-control-solid mb-3 mb-lg-0"
                placeholder="Name"
                aria-label="Name"
                {...formik.getFieldProps("name")}
              />
              {formik.touched.name && formik.errors.name && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{formik.errors.name}</div>
                </div>
              )}
            </div>
            <div className="mb-6">
              <label className="form-label fw-bold">Image upload<span className="red-asterisk">*</span></label>
              <div>
                <label
                  htmlFor="company-img"
                  className="form-control form-control-lg form-control-solid mb-3 mb-lg-0 cursor-pointer"
                >
                  {imgFileName
                    ? charValidate(imgFileName, 25)
                    : "Choose File"}
                </label>
                <input
                  id="company-img"
                  type="file"
                  accept="image/*"
                  className="form-control form-control-lg form-control-solid mb-3 mb-lg-0"
                  placeholder="Upload Image"
                  aria-label="Upload Image"
                  onChange={handleChangeImageUpload}
                  style={{
                    display: "none",
                  }}
                />
                {formik.touched.image && formik.errors.image && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">{formik.errors.image}</div>
                  </div>
                )}
              </div>
              {imgUploading && (
                <div className="d-flex justify-content-center m-5">
                  <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              )}
              {(companyImg || editData?.image) && (
                <div style={{
                  textAlign: "center",
                  marginTop: "1rem",
                  position: "relative",
                }}>
                  <img
                    src={`${APP_URLs.awsS3ImageURL}${companyImg || editData?.image}`}
                    style={{
                      maxWidth: "200px",
                      margin: "0 auto",
                      padding: "1rem",
                      border: "1px solid #eeeeee",
                    }}
                    alt="Preview"
                  />
                  <CloseIcon
                    className="close-icon"
                    onClick={removeImg}
                  />
                </div>
              )}
            </div>
            <div className="w-100 d-flex align-items-center justify-content-end mt-5">
              <Button
                onClick={() => {
                  formik.resetForm();
                  setImgFileName("");
                  setCompanyImg("");
                  handleClose(false);
                }}
                disabled={imgUploading}
                size="lg"
                variant="secondary"
              >
                Cancel
              </Button>
              <div className="w-15px"></div>
              <Button type="submit" size="lg" variant="primary" disabled={imgUploading}>
                {loading ? "Loading..." : "Submit"}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </Box>
  );
};

export default AddEditCompany;
